/**
 * The property panel of an assembly sheet, as real HTML.
 *
 * On the backend-rendered PNG this block is part of the picture; here it is a plain
 * scrollable column of text, so the customer can select a holder name or a comment and
 * paste it somewhere, and so the words stay at reading size no matter how far the drawing
 * next door is zoomed in.
 *
 * The two-column rhythm, the ruled section headings and the closing "dimensions in mm"
 * note follow the backend panel, so the two read as the same document.
 */

import { SECTION_LABEL } from '@/components/common/surfaces';
import { panelRowLabel, panelRowValue, panelSectionLabel } from '@/components/dialogs/assemblyDrawing/drawingLabels';
import type { PanelSection } from '@/types/assemblyDrawing.types';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface AssemblyPropertyPanelProps {
  panel: PanelSection[];
  className?: string;
}

export const AssemblyPropertyPanel: FC<AssemblyPropertyPanelProps> = ({ panel, className = '' }) => {
  const { t } = useTranslation();

  return (
    // `overscroll-contain`: reaching either end of this column must not hand the gesture
    // on to whatever is behind the modal.
    <aside
      aria-label={t('toolManagement.assemblyDrawing.panelLabel')}
      className={`overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-gray-50 ${className}`}
    >
      <div className="px-4 py-4">
        {panel.map((section, index) => (
          <section key={section.section_key} className={index === 0 ? '' : 'mt-6'}>
            <h3 className={`${SECTION_LABEL} border-b border-gray-200 pb-2`}>
              {panelSectionLabel(section.section_key, t)}
            </h3>
            <dl className="mt-2 grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
              {section.rows.map((row) => (
                <div key={`${section.section_key}-${row.key}`} className="contents">
                  <dt className="text-gray-500">{panelRowLabel(row, t)}</dt>
                  <dd className="break-words tabular-nums text-gray-900">{panelRowValue(row, t)}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
        <p className="mt-6 text-xs text-gray-500">{t('toolManagement.assemblyDrawing.unitsNote')}</p>
      </div>
    </aside>
  );
};
